import { createAuthenticatedApiClient, setAuthHeader } from '../config/api';

/**
 * Admin Service
 * 
 * Provides methods for admin-specific operations like user management,
 * system statistics, and activity logs.
 */

// Create authenticated API client for admin operations
const apiClient = createAuthenticatedApiClient();

// Types for admin API responses
interface SystemStats {
  totalUsers: number;
  totalNotes: number;
  totalDisplays: number;
  activeDisplays: number;
  pendingModeration: number;
  usersLastWeek: number;
  notesLastWeek: number;
  displayActivity: {
    region: string;
    displayCount: number;
    noteCount: number;
    sessionCount: number;
  }[];
}

// Backend response format from the admin/stats endpoint
interface BackendStatsResponse {
  success: boolean;
  data: {
    displays: {
      total: number;
      active: number;
      inactive: number;
      mappableDisplays: any[];
    };
    notes: {
      total: number;
      pending: number;
      approved: number;
      rejected: number;
    };
    sessions: {
      active: number;
    };
    analytics: {
      notesViewed: number;
      qrScans: number;
    };
  };
}

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  aliasName?: string;
  role: 'contributor' | 'moderator' | 'admin';
  subscriptionTier: 'free' | 'individual' | 'group' | 'organization' | 'custom';
  createdAt: string;
  updatedAt: string;
  location?: {
    zipcode?: string;
    city?: string;
    state?: string;
  };
  emailPreferences?: {
    newsletter: boolean;
    notifyOnInspiration: boolean;
  };
}

// Backend response format from the admin/users endpoint
interface BackendUsersResponse {
  success: boolean;
  data: {
    users: User[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  };
}

interface ActivityLog {
  _id: string;
  logType: 'security' | 'system';
  eventType: string;
  userId?: string;
  userEmail?: string;
  description: string;
  metadata?: any;
  timestamp: string;
  ipAddress?: string;
  userAgent?: string;
}

interface ActivityLogsResponse {
  success: boolean;
  data: {
    logs: ActivityLog[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  };
  message?: string;
}

interface CreateModeratorRequest {
  email: string;
  firstName: string;
  lastName: string;
  subscriptionTier: 'individual' | 'group' | 'organization' | 'custom';
}

interface UpdateUserProfileRequest {
  firstName?: string;
  lastName?: string;
  aliasName?: string;
  role?: 'contributor' | 'moderator' | 'admin';
  subscriptionTier?: 'free' | 'individual' | 'group' | 'organization' | 'custom';
  emailPreferences?: {
    newsletter?: boolean;
    notifyOnInspiration?: boolean;
  };
  location?: {
    zipcode?: string;
    city?: string;
    state?: string;
  };
}

interface UpdateNoteQualityRequest {
  quality_score: number;
}

interface UsersResponse {
  success: boolean;
  data: User[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export class AdminService {
  /**
   * Get system-wide statistics for admin dashboard
   */
  static async getSystemStats(): Promise<SystemStats> {
    try {
      // Ensure auth headers are set
      setAuthHeader(apiClient);
      
      const response = await apiClient.get<BackendStatsResponse>('/admin/stats');
      
      // The backend returns a nested structure with success/data
      // We need to map it to the expected SystemStats format
      const backendData = response.data.data;
      
      // Map the backend response to the expected SystemStats interface
      const systemStats: SystemStats = {
        totalUsers: 0, // This data is not available from current endpoint
        totalNotes: backendData.notes?.total || 0,
        totalDisplays: backendData.displays?.total || 0,
        activeDisplays: backendData.displays?.active || 0,
        pendingModeration: backendData.notes?.pending || 0,
        usersLastWeek: 0, // This data is not available from current endpoint
        notesLastWeek: 0, // This data is not available from current endpoint
        displayActivity: [] // This data is not available from current endpoint
      };
      
      return systemStats;
    } catch (error) {
      console.error('Error fetching system stats:', error);
      throw error;
    }
  }

  /**
   * Get users with filtering and pagination
   */
  static async getUsers(
    filters: {
      search?: string;
      role?: 'contributor' | 'moderator' | 'admin';
      tier?: 'free' | 'individual' | 'group' | 'organization' | 'custom';
      page?: number;
      limit?: number;
      sort?: 'createdAt' | 'updatedAt' | 'email' | 'lastName';
    } = {}
  ): Promise<UsersResponse> {
    try {
      // Ensure auth headers are set
      setAuthHeader(apiClient);
      
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.role) params.append('role', filters.role);
      if (filters.tier) params.append('tier', filters.tier);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.sort) params.append('sort', filters.sort);

      const response = await apiClient.get<BackendUsersResponse>(
        `/admin/users?${params}`
      );

      // Map the backend response to the expected frontend format
      const backendData = response.data.data;
      
      const usersResponse: UsersResponse = {
        success: response.data.success,
        data: backendData.users,
        pagination: backendData.pagination
      };

      return usersResponse;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  /**
   * Update a user's profile (admin can edit any user)
   */
  static async updateUserProfile(
    userId: string,
    profileData: UpdateUserProfileRequest
  ): Promise<any> {
    try {
      // Ensure auth headers are set
      setAuthHeader(apiClient);
      
      const response = await apiClient.put(
        `/admin/users/${userId}/profile`,
        profileData
      );

      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  /**
   * Create a new moderator account
   */
  static async createModerator(
    moderatorData: CreateModeratorRequest
  ): Promise<any> {
    try {
      // Ensure auth headers are set
      setAuthHeader(apiClient);
      
      const response = await apiClient.post(
        '/admin/moderators',
        moderatorData
      );

      return response.data;
    } catch (error) {
      console.error('Error creating moderator:', error);
      throw error;
    }
  }

  /**
   * Update a note's quality score (admin override)
   */
  static async updateNoteQuality(
    noteId: string,
    qualityData: UpdateNoteQualityRequest
  ): Promise<any> {
    try {
      // Ensure auth headers are set
      setAuthHeader(apiClient);
      
      const response = await apiClient.put(
        `/admin/notes/${noteId}/quality`,
        qualityData
      );

      return response.data;
    } catch (error) {
      console.error('Error updating note quality:', error);
      throw error;
    }
  }

  /**
   * Get activity logs with filtering
   */
  static async getActivityLogs(
    filters: {
      logType: 'security' | 'system';
      eventType?: string;
      userId?: string;
      startDate?: string;
      endDate?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<ActivityLogsResponse> {
    try {
      // Ensure auth headers are set
      setAuthHeader(apiClient);
      
      const params = new URLSearchParams();
      
      params.append('logType', filters.logType);
      if (filters.eventType) params.append('eventType', filters.eventType);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const response = await apiClient.get<ActivityLogsResponse>(
        `/admin/activity?${params}`
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      throw error;
    }
  }

  /**
   * Export activity logs as CSV
   */
  static async exportActivityLogs(
    filters: {
      logType: 'security' | 'system';
      eventType?: string;
      userId?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<Blob> {
    try {
      // Ensure auth headers are set
      setAuthHeader(apiClient);
      
      const params = new URLSearchParams();
      
      params.append('logType', filters.logType);
      if (filters.eventType) params.append('eventType', filters.eventType);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      params.append('format', 'csv');

      const response = await apiClient.get<Blob>(
        `/admin/activity?${params}`,
        {
          responseType: 'blob'
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error exporting activity logs:', error);
      throw error;
    }
  }
} 